import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';

const CustomerForm = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [interactions, setInteractions] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingInteractions, setLoadingInteractions] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    customer_type: 'person',
    first_name: '',
    last_name: '',
    company_name: '',
    vat_number: '',
    phone: '',
    location_city: '',
    location_state: '',
    location_country: '',
    vip_tier: 'bronze',
    customer_segment: 'new_prospect',
    is_active: true
  });

  const isEditing = Boolean(customerId);

  const fetchOrders = useCallback(async () => {
    if (!customerId) return;
    try {
      setLoadingOrders(true);
      const response = await api.get(`/customers/${customerId}/orders`);
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoadingOrders(false);
    }
  }, [customerId]);

  const fetchInteractions = useCallback(async () => {
    if (!customerId) return;
    try {
      setLoadingInteractions(true);
      const response = await api.get(`/customers/${customerId}/interactions`);
      setInteractions(response.data.interactions || []);
    } catch (error) {
      console.error('Error fetching interactions:', error);
    } finally {
      setLoadingInteractions(false);
    }
  }, [customerId]);

  const fetchCustomer = useCallback(async () => {
    if (!customerId) return;
    try {
      setLoading(true);
      const response = await api.get(`/customers/${customerId}`);
      const customerData = response.data;
      setCustomer(customerData);
      setFormData({
        email: customerData.email || '',
        customer_type: customerData.customer_type || 'person',
        first_name: customerData.first_name || '',
        last_name: customerData.last_name || '',
        company_name: customerData.company_name || '',
        vat_number: customerData.vat_number || '',
        phone: customerData.phone || '',
        location_city: customerData.location_city || '',
        location_state: customerData.location_state || '',
        location_country: customerData.location_country || '',
        vip_tier: customerData.vip_tier || 'bronze',
        customer_segment: customerData.customer_segment || 'new_prospect',
        is_active: customerData.is_active !== undefined ? customerData.is_active : true
      });
    } catch (error) {
      console.error('Error fetching customer:', error);
      setError('Failed to load customer');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (customerId) {
      fetchCustomer();
      fetchOrders();
      fetchInteractions();
    }
  }, [customerId, fetchCustomer, fetchOrders, fetchInteractions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Clean up formData - remove empty strings and convert to null where needed
      const cleanedData = Object.entries(formData).reduce((acc, [key, value]) => {
        if (value === '') {
          acc[key] = null;
        } else {
          acc[key] = value;
        }
        return acc;
      }, {});

      if (isEditing) {
        await api.put(`/customers/${customerId}`, cleanedData);
      } else {
        await api.post('/customers', cleanedData);
      }
      navigate('/customers');
    } catch (error) {
      console.error('Error saving customer:', error);
      setError(error.response?.data?.error || error.response?.data?.message || `Failed to ${isEditing ? 'update' : 'create'} customer`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to permanently delete this customer? This action cannot be undone.')) {
      return;
    }
    
    try {
      setLoading(true);
      await api.delete(`/customers/${customerId}`);
      navigate('/customers');
    } catch (error) {
      console.error('Error deleting customer:', error);
      setError(error.response?.data?.error || 'Failed to delete customer');
      setLoading(false);
    }
  };

  if (loading && isEditing && !customer) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="breadcrumb">
        <button onClick={() => navigate('/dashboard')} className="breadcrumb-link">Dashboard</button>
        <span className="breadcrumb-separator">/</span>
        <button onClick={() => navigate('/customers')} className="breadcrumb-link">Customers</button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">{isEditing ? 'Edit Customer' : 'New Customer'}</span>
      </nav>

      {/* Main Card */}
      <div className="card">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0'
            }}>
              {isEditing ? '✏️ Edit Customer' : '➕ New Customer'}
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              {isEditing ? 'Update customer information and tracking' : 'Add a new customer to your database'}
            </p>
          </div>
        </div>

        {error && (
          <div style={{
            backgroundColor: 'var(--red-50)',
            border: '1px solid var(--red-200)',
            color: 'var(--red-800)',
            padding: '0.75rem',
            borderRadius: '8px',
            marginBottom: '1rem'
          }}>
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ 
          display: 'flex', 
          gap: '1rem', 
          justifyContent: 'space-between',
          marginBottom: '1.5rem',
          marginTop: '5px',
          paddingBottom: '1.5rem',
          borderBottom: '1px solid var(--gray-200)'
        }}>
          <div style={{ display: 'flex', gap: '1rem' }}>
            {isEditing && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={loading}
                className="btn btn-danger"
              >
                🗑️ Delete
              </button>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              type="button"
              onClick={() => navigate('/customers')}
              disabled={loading}
              className="btn btn-outline"
            >
              ✕ Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary"
              form="customer-form"
            >
              {loading ? 'Saving...' : (isEditing ? '💾 Update' : '➕ Create Customer')}
            </button>
          </div>
        </div>

        <form id="customer-form" onSubmit={handleSubmit}>
          
          {/* Contact Information Section */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ 
              fontSize: '1rem', 
              fontWeight: '600', 
              color: 'var(--gray-800)', 
              marginBottom: '1rem',
              paddingBottom: '0.5rem',
              borderBottom: '2px solid var(--gray-200)'
            }}>
              📧 Contact Information
            </h3>
            
            {/* Customer Type Radio Buttons */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.75rem', 
                fontWeight: '600', 
                color: 'var(--gray-700)',
                fontSize: '0.875rem'
              }}>
                Customer Type *
              </label>
              <div style={{ display: 'flex', gap: '1.5rem' }}>
                <label style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  cursor: 'pointer'
                }}>
                  <input 
                    type="radio" 
                    name="customer_type"
                    value="person"
                    checked={formData.customer_type === 'person'}
                    onChange={handleInputChange}
                    style={{ margin: 0, cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '1.25rem' }}>👤</span>
                  <span style={{ fontWeight: '500', color: 'var(--gray-900)' }}>Individual Person</span>
                </label>
                <label style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  cursor: 'pointer'
                }}>
                  <input 
                    type="radio" 
                    name="customer_type"
                    value="company"
                    checked={formData.customer_type === 'company'}
                    onChange={handleInputChange}
                    style={{ margin: 0, cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '1.25rem' }}>🏢</span>
                  <span style={{ fontWeight: '500', color: 'var(--gray-900)' }}>Company</span>
                </label>
              </div>
            </div>

            {formData.customer_type === 'person' ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '0.5rem', 
                      fontWeight: '600', 
                      color: 'var(--gray-700)',
                      fontSize: '0.875rem'
                    }}>
                      First Name
                    </label>
                    <input 
                      type="text" 
                      name="first_name"
                      value={formData.first_name} 
                      onChange={handleInputChange}
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '0.5rem', 
                      fontWeight: '600', 
                      color: 'var(--gray-700)',
                      fontSize: '0.875rem'
                    }}>
                      Last Name
                    </label>
                    <input 
                      type="text" 
                      name="last_name"
                      value={formData.last_name} 
                      onChange={handleInputChange}
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '0.5rem', 
                      fontWeight: '600', 
                      color: 'var(--gray-700)',
                      fontSize: '0.875rem'
                    }}>
                      Email Address *
                    </label>
                    <input 
                      type="email" 
                      name="email"
                      required 
                      value={formData.email} 
                      onChange={handleInputChange}
                      placeholder="customer@example.com"
                    />
                  </div>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '0.5rem', 
                      fontWeight: '600', 
                      color: 'var(--gray-700)',
                      fontSize: '0.875rem'
                    }}>
                      Phone Number
                    </label>
                    <input 
                      type="tel" 
                      name="phone"
                      value={formData.phone} 
                      onChange={handleInputChange}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>
              </>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '0.5rem', 
                      fontWeight: '600', 
                      color: 'var(--gray-700)',
                      fontSize: '0.875rem'
                    }}>
                      Company Name *
                    </label>
                    <input 
                      type="text" 
                      name="company_name"
                      required={formData.customer_type === 'company'}
                      value={formData.company_name} 
                      onChange={handleInputChange}
                      placeholder="Acme Corporation"
                    />
                  </div>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '0.5rem', 
                      fontWeight: '600', 
                      color: 'var(--gray-700)',
                      fontSize: '0.875rem'
                    }}>
                      VAT Number
                    </label>
                    <input 
                      type="text" 
                      name="vat_number"
                      value={formData.vat_number} 
                      onChange={handleInputChange}
                      placeholder="GB123456789"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '0.5rem', 
                      fontWeight: '600', 
                      color: 'var(--gray-700)',
                      fontSize: '0.875rem'
                    }}>
                      Email Address *
                    </label>
                    <input 
                      type="email" 
                      name="email"
                      required 
                      value={formData.email} 
                      onChange={handleInputChange}
                      placeholder="customer@example.com"
                    />
                  </div>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '0.5rem', 
                      fontWeight: '600', 
                      color: 'var(--gray-700)',
                      fontSize: '0.875rem'
                    }}>
                      Phone Number
                    </label>
                    <input 
                      type="tel" 
                      name="phone"
                      value={formData.phone} 
                      onChange={handleInputChange}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Location Information Section */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ 
              fontSize: '1rem', 
              fontWeight: '600', 
              color: 'var(--gray-800)', 
              marginBottom: '1rem',
              paddingBottom: '0.5rem',
              borderBottom: '2px solid var(--gray-200)'
            }}>
              📍 Location
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem' }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '600', 
                  color: 'var(--gray-700)',
                  fontSize: '0.875rem'
                }}>
                  City
                </label>
                <input 
                  type="text" 
                  name="location_city"
                  value={formData.location_city} 
                  onChange={handleInputChange}
                  placeholder="San Francisco"
                />
              </div>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '600', 
                  color: 'var(--gray-700)',
                  fontSize: '0.875rem'
                }}>
                  State/Province
                </label>
                <input 
                  type="text" 
                  name="location_state"
                  value={formData.location_state} 
                  onChange={handleInputChange}
                  placeholder="California"
                />
              </div>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '600', 
                  color: 'var(--gray-700)',
                  fontSize: '0.875rem'
                }}>
                  Country
                </label>
                <input 
                  type="text" 
                  name="location_country"
                  value={formData.location_country} 
                  onChange={handleInputChange}
                  placeholder="United States"
                />
              </div>
            </div>
          </div>

          {/* Customer Segmentation Section */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ 
              fontSize: '1rem', 
              fontWeight: '600', 
              color: 'var(--gray-800)', 
              marginBottom: '1rem',
              paddingBottom: '0.5rem',
              borderBottom: '2px solid var(--gray-200)'
            }}>
              🎯 Segmentation & Status
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem' }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '600', 
                  color: 'var(--gray-700)',
                  fontSize: '0.875rem'
                }}>
                  VIP Tier
                </label>
                <select 
                  name="vip_tier"
                  value={formData.vip_tier} 
                  onChange={handleInputChange}
                >
                  <option value="bronze">🥉 Bronze</option>
                  <option value="silver">🥈 Silver</option>
                  <option value="gold">🥇 Gold</option>
                  <option value="platinum">💎 Platinum</option>
                </select>
              </div>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '600', 
                  color: 'var(--gray-700)',
                  fontSize: '0.875rem'
                }}>
                  Customer Segment
                </label>
                <select 
                  name="customer_segment"
                  value={formData.customer_segment} 
                  onChange={handleInputChange}
                >
                  <option value="new_prospect">New Prospect</option>
                  <option value="active">Active</option>
                  <option value="at_risk">At Risk</option>
                  <option value="churned">Churned</option>
                  <option value="loyal">Loyal</option>
                  <option value="vip">VIP</option>
                </select>
              </div>
              <div>
                <label style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontWeight: '600', 
                  color: 'var(--gray-700)',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  marginTop: '1.75rem'
                }}>
                  <input 
                    type="checkbox" 
                    name="is_active"
                    checked={formData.is_active} 
                    onChange={handleInputChange}
                    style={{ width: '1.25rem', height: '1.25rem' }}
                  />
                  Active Customer
                </label>
              </div>
            </div>
          </div>

          {/* Customer Metrics - Only show when editing */}
          {isEditing && customer && (
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ 
                fontSize: '1rem', 
                fontWeight: '600', 
                color: 'var(--gray-800)', 
                marginBottom: '1rem',
                paddingBottom: '0.5rem',
                borderBottom: '2px solid var(--gray-200)'
              }}>
                📊 Customer Metrics
              </h3>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
                <div style={{ 
                  padding: '1rem', 
                  backgroundColor: 'var(--gray-50)', 
                  borderRadius: '8px',
                  border: '1px solid var(--gray-200)'
                }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)', marginBottom: '0.25rem' }}>
                    Total Spent
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--gray-900)' }}>
                    ${(customer.total_spent || 0).toLocaleString()}
                  </div>
                </div>
                <div style={{ 
                  padding: '1rem', 
                  backgroundColor: 'var(--gray-50)', 
                  borderRadius: '8px',
                  border: '1px solid var(--gray-200)'
                }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)', marginBottom: '0.25rem' }}>
                    Total Orders
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--gray-900)' }}>
                    {customer.total_orders || 0}
                  </div>
                </div>
                <div style={{ 
                  padding: '1rem', 
                  backgroundColor: 'var(--gray-50)', 
                  borderRadius: '8px',
                  border: '1px solid var(--gray-200)'
                }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)', marginBottom: '0.25rem' }}>
                    Avg Order Value
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--gray-900)' }}>
                    ${(customer.average_order_value || 0).toLocaleString()}
                  </div>
                </div>
                <div style={{ 
                  padding: '1rem', 
                  backgroundColor: 'var(--gray-50)', 
                  borderRadius: '8px',
                  border: '1px solid var(--gray-200)'
                }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)', marginBottom: '0.25rem' }}>
                    Loyalty Points
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--gray-900)' }}>
                    {customer.loyalty_points || 0}
                  </div>
                </div>
              </div>
            </div>
          )}
        </form>
      </div>

      {/* Tabs - Only show when editing */}
      {isEditing && (
        <div className="card" style={{ marginTop: '1.5rem' }}>
          <div style={{ 
            display: 'flex', 
            gap: '2rem', 
            borderBottom: '2px solid var(--gray-200)',
            marginBottom: '1.5rem'
          }}>
            <button
              type="button"
              onClick={() => setActiveTab('orders')}
              style={{
                background: 'none',
                border: 'none',
                padding: '1rem 0',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                color: activeTab === 'orders' ? 'var(--primary-600)' : 'var(--gray-600)',
                borderBottom: activeTab === 'orders' ? '2px solid var(--primary-600)' : '2px solid transparent',
                marginBottom: '-2px',
                transition: 'all 0.2s'
              }}
            >
              🛒 Orders ({orders.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('interactions')}
              style={{
                background: 'none',
                border: 'none',
                padding: '1rem 0',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                color: activeTab === 'interactions' ? 'var(--primary-600)' : 'var(--gray-600)',
                borderBottom: activeTab === 'interactions' ? '2px solid var(--primary-600)' : '2px solid transparent',
                marginBottom: '-2px',
                transition: 'all 0.2s'
              }}
            >
              📊 Interactions ({interactions.length})
            </button>
          </div>

          {/* Orders Tab Content */}
          {activeTab === 'orders' && (
            <div style={{ minHeight: '300px' }}>
              {loadingOrders ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--gray-600)' }}>
                  Loading orders...
                </div>
              ) : orders.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '3rem',
                  backgroundColor: 'var(--gray-50)',
                  borderRadius: '8px',
                  border: '1px dashed var(--gray-300)'
                }}>
                  <p style={{ fontSize: '3rem', margin: '0 0 1rem 0' }}>🛒</p>
                  <p style={{ color: 'var(--gray-600)', fontSize: '1rem', margin: 0 }}>
                    No orders yet
                  </p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Order ID</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Date</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Total</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order, index) => (
                        <tr key={index} style={{ borderBottom: '1px solid var(--gray-200)' }}>
                          <td style={{ padding: '0.75rem' }}>#{order.id}</td>
                          <td style={{ padding: '0.75rem' }}>
                            {order.created_at ? new Date(order.created_at).toLocaleDateString() : '-'}
                          </td>
                          <td style={{ padding: '0.75rem' }}>${(order.total_amount || 0).toFixed(2)}</td>
                          <td style={{ padding: '0.75rem' }}>
                            <span className={`badge badge-${order.status === 'completed' ? 'success' : order.status === 'pending' ? 'warning' : 'default'}`}>
                              {order.status || 'pending'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Interactions Tab Content */}
          {activeTab === 'interactions' && (
            <div style={{ minHeight: '300px' }}>
              {loadingInteractions ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--gray-600)' }}>
                  Loading interactions...
                </div>
              ) : interactions.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '3rem',
                  backgroundColor: 'var(--gray-50)',
                  borderRadius: '8px',
                  border: '1px dashed var(--gray-300)'
                }}>
                  <p style={{ fontSize: '3rem', margin: '0 0 1rem 0' }}>📊</p>
                  <p style={{ color: 'var(--gray-600)', fontSize: '1rem', margin: 0 }}>
                    No interactions recorded yet
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {interactions.map((interaction, index) => {
                    const interactionIcons = {
                      email_open: '📧',
                      click: '🖱️',
                      purchase: '💰',
                      page_view: '👁️',
                      form_submit: '📝'
                    };
                    
                    const icon = interactionIcons[interaction.interaction_type] || '•';
                    const date = new Date(interaction.created_at);
                    
                    return (
                      <div 
                        key={index}
                        style={{
                          padding: '1.25rem',
                          backgroundColor: 'white',
                          border: '1px solid var(--gray-200)',
                          borderRadius: '8px',
                          display: 'flex',
                          gap: '1rem',
                          transition: 'box-shadow 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'}
                        onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
                      >
                        <div style={{ 
                          fontSize: '1.5rem',
                          flexShrink: 0
                        }}>
                          {icon}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            fontWeight: '600', 
                            color: 'var(--gray-900)',
                            marginBottom: '0.25rem',
                            textTransform: 'capitalize'
                          }}>
                            {interaction.interaction_type.replace(/_/g, ' ')}
                          </div>
                          {interaction.channel && (
                            <div style={{ 
                              color: 'var(--gray-700)',
                              fontSize: '0.875rem',
                              marginBottom: '0.25rem'
                            }}>
                              Channel: {interaction.channel}
                            </div>
                          )}
                          {interaction.value && (
                            <div style={{ 
                              color: 'var(--success-600)',
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              marginBottom: '0.5rem'
                            }}>
                              Value: ${interaction.value.toFixed(2)}
                            </div>
                          )}
                          <div style={{ 
                            color: 'var(--gray-500)',
                            fontSize: '0.75rem'
                          }}>
                            {date.toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerForm;
